import client, { unwrap } from './client';

/**
 * One function per endpoint. Components never build URLs themselves, so a route
 * change is a one-line edit here rather than a hunt through the pages.
 */

export const authApi = {
  login: (payload) => unwrap(client.post('/auth/login', payload)),
  logout: () => unwrap(client.post('/auth/logout')),
  logoutAll: () => unwrap(client.post('/auth/logout-all')),
  me: () => unwrap(client.get('/auth/me')),
  refresh: () => unwrap(client.post('/auth/refresh')),
  forgotPassword: (payload) => unwrap(client.post('/auth/forgot-password', payload)),
  resetPassword: (payload) => unwrap(client.post('/auth/reset-password', payload)),
  acceptInvite: (payload) => unwrap(client.post('/auth/accept-invite', payload)),
  changePassword: (payload) => unwrap(client.post('/auth/change-password', payload)),
};

export const dashboardApi = {
  overview: () => unwrap(client.get('/dashboard')),
};

export const providerApi = {
  list: (params) => unwrap(client.get('/providers', { params })),
  options: () => unwrap(client.get('/providers/options')),
  get: (id) => unwrap(client.get(`/providers/${id}`)),
  create: (payload) => unwrap(client.post('/providers', payload)),
  update: (id, payload) => unwrap(client.patch(`/providers/${id}`, payload)),
  remove: (id) => unwrap(client.delete(`/providers/${id}`)),
  // Re-sends the set-a-password link to a guru who never activated their login.
  resendInvite: (id) => unwrap(client.post(`/providers/${id}/resend-invite`)),
  decide: (id, payload) => unwrap(client.patch(`/providers/${id}/decision`, payload)),
  // The Java API's route is /status; /account-status was the Node backend's and 404s now.
  setAccountStatus: (id, payload) => unwrap(client.patch(`/providers/${id}/status`, payload)),
  terms: (id) => unwrap(client.get(`/providers/${id}/terms`)),
  plans: (id) => unwrap(client.get(`/providers/${id}/plans`)),
  updateTerms: (id, payload) => unwrap(client.patch(`/providers/${id}/terms`, payload)),
  updatePayout: (id, payload) => unwrap(client.patch(`/providers/${id}/payout`, payload)),
  addGalleryItem: (id, payload) => unwrap(client.post(`/providers/${id}/gallery`, payload)),
  removeGalleryItem: (id, itemId) => unwrap(client.delete(`/providers/${id}/gallery/${itemId}`)),

  documents: {
    list: (providerId, params) => unwrap(client.get(`/providers/${providerId}/documents`, { params })),
    create: (providerId, payload) => unwrap(client.post(`/providers/${providerId}/documents`, payload)),
    review: (providerId, id, payload) =>
      unwrap(client.patch(`/providers/${providerId}/documents/${id}/review`, payload)),
    remove: (providerId, id) => unwrap(client.delete(`/providers/${providerId}/documents/${id}`)),
  },

  sessions: {
    list: (providerId, params) => unwrap(client.get(`/providers/${providerId}/sessions`, { params })),
    create: (providerId, payload) => unwrap(client.post(`/providers/${providerId}/sessions`, payload)),
    update: (providerId, id, payload) => unwrap(client.patch(`/providers/${providerId}/sessions/${id}`, payload)),
    cancel: (providerId, id, payload) =>
      unwrap(client.patch(`/providers/${providerId}/sessions/${id}/cancel`, payload)),
    remove: (providerId, id) => unwrap(client.delete(`/providers/${providerId}/sessions/${id}`)),
  },

  team: {
    list: (providerId, params) => unwrap(client.get(`/providers/${providerId}/team`, { params })),
    create: (providerId, payload) => unwrap(client.post(`/providers/${providerId}/team`, payload)),
    update: (providerId, id, payload) => unwrap(client.patch(`/providers/${providerId}/team/${id}`, payload)),
    remove: (providerId, id) => unwrap(client.delete(`/providers/${providerId}/team/${id}`)),
  },

  report: {
    get: (providerId, params) => unwrap(client.get(`/providers/${providerId}/report`, { params })),
    recalculate: (providerId, payload) =>
      unwrap(client.post(`/providers/${providerId}/report/recalculate`, payload)),
    payments: (providerId, params) => unwrap(client.get(`/providers/${providerId}/report/payments`, { params })),
    recordPayment: (providerId, payload) =>
      unwrap(client.post(`/providers/${providerId}/report/payments`, payload)),
    voidPayment: (providerId, paymentId, payload) =>
      unwrap(client.patch(`/providers/${providerId}/report/payments/${paymentId}/void`, payload)),
    invoices: (providerId) => unwrap(client.get(`/providers/${providerId}/report/invoices`)),
    raiseInvoice: (providerId) => unwrap(client.post(`/providers/${providerId}/report/invoices`)),
    markInvoicePaid: (providerId, invoiceId, payload) =>
      unwrap(client.patch(`/providers/${providerId}/report/invoices/${invoiceId}/pay`, payload)),
    voidInvoice: (providerId, invoiceId, payload) =>
      unwrap(client.patch(`/providers/${providerId}/report/invoices/${invoiceId}/void`, payload)),
    // Fetched per click rather than carried on every invoice in the list, so one
    // response cannot hand back a payable URL for every invoice at once.
    invoicePaymentLink: (providerId, invoiceId) =>
      unwrap(client.get(`/providers/${providerId}/report/invoices/${invoiceId}/payment-link`)),
  },
};

export const categoryApi = {
  list: (params) => unwrap(client.get('/categories', { params })),
  options: () => unwrap(client.get('/categories/options')),
  get: (id) => unwrap(client.get(`/categories/${id}`)),
  create: (payload) => unwrap(client.post('/categories', payload)),
  update: (id, payload) => unwrap(client.patch(`/categories/${id}`, payload)),
  remove: (id) => unwrap(client.delete(`/categories/${id}`)),
};

export const subcategoryApi = {
  list: (params) => unwrap(client.get('/subcategories', { params })),
  grouped: () => unwrap(client.get('/subcategories/grouped')),
  options: (params) => unwrap(client.get('/subcategories/options', { params })),
  create: (payload) => unwrap(client.post('/subcategories', payload)),
  update: (id, payload) => unwrap(client.patch(`/subcategories/${id}`, payload)),
  remove: (id) => unwrap(client.delete(`/subcategories/${id}`)),
};

export const countryApi = {
  list: (params) => unwrap(client.get('/countries', { params })),
  options: () => unwrap(client.get('/countries/options')),
  create: (payload) => unwrap(client.post('/countries', payload)),
  update: (id, payload) => unwrap(client.patch(`/countries/${id}`, payload)),
  toggleStatus: (id) => unwrap(client.patch(`/countries/${id}/toggle-status`)),
  remove: (id) => unwrap(client.delete(`/countries/${id}`)),
};

export const roleApi = {
  list: () => unwrap(client.get('/roles')),
  options: () => unwrap(client.get('/roles/options')),
  permissions: () => unwrap(client.get('/roles/permissions')),
  get: (id) => unwrap(client.get(`/roles/${id}`)),
  create: (payload) => unwrap(client.post('/roles', payload)),
  update: (id, payload) => unwrap(client.patch(`/roles/${id}`, payload)),
  remove: (id) => unwrap(client.delete(`/roles/${id}`)),
};

/**
 * The shared plan catalogue.
 *
 * Distinct from `providerApi.plans(id)`, which is one provider's assignment
 * HISTORY. This is the menu those assignments are chosen from.
 */
export const planApi = {
  // activeOnly omits archived plans -- what a picker wants, as opposed to the
  // management screen which shows everything.
  list: (params) => unwrap(client.get('/plans', { params })),
  models: () => unwrap(client.get('/plans/models')),
  get: (id) => unwrap(client.get(`/plans/${id}`)),
  create: (payload) => unwrap(client.post('/plans', payload)),
  update: (id, payload) => unwrap(client.patch(`/plans/${id}`, payload)),
  remove: (id) => unwrap(client.delete(`/plans/${id}`)),
};

export const teamApi = {
  list: (params) => unwrap(client.get('/team', { params })),
  summary: () => unwrap(client.get('/team/summary')),
  get: (id) => unwrap(client.get(`/team/${id}`)),
  invite: (payload) => unwrap(client.post('/team', payload)),
  resendInvite: (id) => unwrap(client.post(`/team/${id}/resend-invite`)),
  update: (id, payload) => unwrap(client.patch(`/team/${id}`, payload)),
  remove: (id) => unwrap(client.delete(`/team/${id}`)),
};

export const couponApi = {
  list: (params) => unwrap(client.get('/coupons', { params })),
  summary: () => unwrap(client.get('/coupons/summary')),
  get: (id) => unwrap(client.get(`/coupons/${id}`)),
  create: (payload) => unwrap(client.post('/coupons', payload)),
  update: (id, payload) => unwrap(client.patch(`/coupons/${id}`, payload)),
  togglePause: (id) => unwrap(client.patch(`/coupons/${id}/toggle-pause`)),
  remove: (id) => unwrap(client.delete(`/coupons/${id}`)),
};

/**
 * Provider-side referral and promo codes. Distinct from couponApi, which
 * discounts a client's booking: these discount what a provider owes the
 * platform and credit the referring provider an earning.
 */
export const referralApi = {
  list: (params) => unwrap(client.get('/referrals', { params })),
  summary: (params) => unwrap(client.get('/referrals/summary', { params })),
  get: (id) => unwrap(client.get(`/referrals/${id}`)),
  forProvider: (providerId) => unwrap(client.get(`/referrals/provider/${providerId}`)),
  // What a provider may still SPEND, as opposed to what forProvider reports they earned.
  balanceFor: (providerId) => unwrap(client.get(`/referrals/provider/${providerId}/balance`)),
  create: (payload) => unwrap(client.post('/referrals', payload)),
  update: (id, payload) => unwrap(client.patch(`/referrals/${id}`, payload)),
  togglePause: (id) => unwrap(client.patch(`/referrals/${id}/toggle-pause`)),
  redeem: (id, payload) => unwrap(client.post(`/referrals/${id}/redeem`, payload)),
  remove: (id) => unwrap(client.delete(`/referrals/${id}`)),
};

/**
 * Paid placement of a class at the top of client search.
 *
 * `listings` is the form's second dropdown -- a guru's own classes, narrowed by
 * the provider chosen in the first. It hangs off /sponsors rather than
 * /providers/:id/sessions so the form needs only sponsor:read to fill itself.
 */
export const sponsorApi = {
  list: (params) => unwrap(client.get('/sponsors', { params })),
  summary: () => unwrap(client.get('/sponsors/summary')),
  listings: (provider) => unwrap(client.get('/sponsors/listings', { params: { provider } })),
  get: (id) => unwrap(client.get(`/sponsors/${id}`)),
  create: (payload) => unwrap(client.post('/sponsors', payload)),
  update: (id, payload) => unwrap(client.patch(`/sponsors/${id}`, payload)),
  remove: (id) => unwrap(client.delete(`/sponsors/${id}`)),
};

/**
 * The two queues on the Queries screen.
 *
 * Separate objects because they are separate resources on the server, and
 * deliberately so: a help desk ticket is a conversation, a plan request is a
 * commercial ask that resolves into an assignment. The console shows them side
 * by side, which is a presentation choice — see the Queries page.
 */
export const supportApi = {
  // activeOnly narrows to Open and With support -- what the queue opens on;
  // requester ('Provider' | 'Client') narrows to tickets gurus or clients raised.
  list: (params) => unwrap(client.get('/support-tickets', { params })),
  openCount: () => unwrap(client.get('/support-tickets/open-count')),
  // Fetching one ticket also marks the requester's messages read, which is what
  // clears its unread badge -- so the list has to be invalidated afterwards.
  get: (id) => unwrap(client.get(`/support-tickets/${id}`)),
  reply: (id, payload) => unwrap(client.post(`/support-tickets/${id}/messages`, payload)),
  update: (id, payload) => unwrap(client.patch(`/support-tickets/${id}`, payload)),
};

/**
 * Direct chat with gurus and their instructors.
 *
 * Distinct from `supportApi` above, and deliberately so: a ticket is a queue entry with a
 * topic, a status and a resolution, and this is an open conversation nobody closes. They are
 * gated by different permissions for the same reason -- an agent who works tickets has not
 * thereby been handed a channel into every studio's inbox.
 *
 * Each operator's conversations are their own, keyed on their admin_users row rather than
 * shared across the desk. A shared inbox would need assignment and handover, which is a
 * different feature.
 */
export const chatApi = {
  conversations: () => unwrap(client.get('/chat/conversations')),
  // Gurus and instructors an operator can open a thread with, narrowed by a search.
  contacts: (search) => unwrap(client.get('/chat/contacts', { params: { search } })),
  // Idempotent: returns the existing thread when the pair already has one.
  start: (participantId, participantType) =>
    unwrap(client.post('/chat/conversations', { participantId, participantType })),
  messages: (id, params) => unwrap(client.get(`/chat/conversations/${id}/messages`, { params })),
  // Anything after a cursor -- what an open window polls.
  since: (id, after) =>
    unwrap(client.get(`/chat/conversations/${id}/messages/since`, { params: { after } })),
  send: (id, body) => unwrap(client.post(`/chat/conversations/${id}/messages`, { body })),
  markRead: (id) => unwrap(client.patch(`/chat/conversations/${id}/read`)),
  unreadCount: () => unwrap(client.get('/chat/unread-count')),
};

/**
 * Plan change requests: a guru asking to move onto different terms.
 *
 * Resolving records the decision only. Assigning the terms raises an invoice and
 * lives on `providerApi.updateTerms` -- an admin agrees here and assigns there
 * when the billing is right, so one click never both agrees and charges.
 */
export const planRequestApi = {
  list: (params) => unwrap(client.get('/plan-requests', { params })),
  openCount: () => unwrap(client.get('/plan-requests/open-count')),
  resolve: (id, payload) => unwrap(client.patch(`/plan-requests/${id}`, payload)),
};

export const reportApi = {
  outstanding: (params) => unwrap(client.get('/reports/outstanding', { params })),
  revenue: (params) => unwrap(client.get('/reports/revenue', { params })),
  // The Revenue screen's four headline figures, scoped to one month.
  revenueSummary: (params) => unwrap(client.get('/reports/revenue-summary', { params })),
  earnings: (params) => unwrap(client.get('/reports/earnings', { params })),
  payouts: (params) => unwrap(client.get('/reports/payouts', { params })),
  // What clients paid one provider over one month — the money behind a payout row.
  // Filtered on when the client PAID, so it deliberately does not tie out against that
  // row's earnings, which are attributed to the month the class is taught.
  clientPayments: (params) => unwrap(client.get('/reports/client-payments', { params })),
  periods: () => unwrap(client.get('/reports/periods')),
  // Rebuilds this month and last month for every active provider. Every figure on these
  // boards comes from the commission ledger, and only the six-hourly job writes to it — so
  // without this the screens can sit at zero for a provider who has genuinely been trading.
  refresh: () => unwrap(client.post('/reports/refresh')),
};

export const insightApi = {
  providers: (params) => unwrap(client.get('/insights/providers', { params })),
  bookings: (params) => unwrap(client.get('/insights/bookings', { params })),
};

export const auditApi = {
  list: (params) => unwrap(client.get('/audit-logs', { params })),
  facets: () => unwrap(client.get('/audit-logs/facets')),
};

export const settingApi = {
  list: () => unwrap(client.get('/settings')),
  update: (payload) => unwrap(client.patch('/settings', payload)),

  /*
   * Checks the stored Teams credentials against Microsoft.
   *
   * Always resolves on a 200 — a wrong client secret comes back as `{ ok: false, reason }`
   * rather than as an HTTP error, because an administrator mid-setup is not a server fault.
   * The caller reads `ok`, not a rejected promise.
   */
  testTeams: () => unwrap(client.post('/settings/integrations/teams/test')),
};

export const uploadApi = {
  limits: () => unwrap(client.get('/uploads/limits')),
  presign: (payload) => unwrap(client.post('/uploads/presign', payload)),
  confirm: (payload) => unwrap(client.post('/uploads/confirm', payload)),
  signedUrl: (key) => unwrap(client.get('/uploads/signed-url', { params: { key } })),
  remove: (key) => unwrap(client.delete('/uploads', { data: { key } })),

  /** Small files go straight through the API as multipart. */
  direct: (kind, file, folder, onProgress) => {
    const form = new FormData();
    form.append('file', file);
    if (folder) form.append('folder', folder);

    return unwrap(
      client.post(`/uploads/${kind}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
      })
    );
  },
};
